import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios, { API } from '../../services/api';
import { Spinner, CheckCircle, Warning, X, FilePdf, Image as ImageIcon, FileText, ArrowClockwise } from '@phosphor-icons/react';

/**
 * UploadProgressTile — single tile tracking one in-flight upload.
 *
 * Lifecycle stages we surface to the user:
 *   (1) "Uploading {pct}%"        — real XHR upload progress (0-95%)
 *   (1b) "Almost done…"            — bytes sent (95-99%) waiting for server response
 *   (2) "Analysing…"               — backend is extracting findings (status=analyzing)
 *   (3) "Ready"                    — extraction_status in {completed, needs_review}
 *       • needs_review surfaces a subtle amber review chip
 *   (4) "Failed" / "Retry"         — on network or extraction failure (with explicit Retry button)
 *
 * Wave 2 fix sprint (2026-02) — fixes the "stuck at 99%" bug:
 *   • XHR has a 120s timeout (was infinite — large files on cellular hung forever).
 *   • ontimeout / onabort / onerror handlers all wired so silent failures surface.
 *   • Upload-phase 95-99% renamed "Almost done…" so users see motion at the boundary.
 *   • If 99% lasts > 25s, user sees a "Stuck? Retry" prompt instead of a frozen bar.
 *   • Failed state now exposes a Retry button that re-fires the upload (no re-pick).
 *   • Mobile Safari cellular handoff: ontimeout + onabort now both clean up and prompt retry.
 */
const fileIcon = (type) => {
  if ((type || '').includes('pdf')) return <FilePdf size={20} className="text-red-500" weight="duotone" />;
  if ((type || '').includes('image')) return <ImageIcon size={20} className="text-blue-500" weight="duotone" />;
  return <FileText size={20} className="text-slate-500" weight="duotone" />;
};

const ANALYZE_STEPS = [
  { label: 'Reading your report',                 sub: 'Pulling out tables, ranges, and flagged values.' },
  { label: 'Identifying key results',             sub: 'Spotting what the lab actually called out.' },
  { label: 'Turning it into plain English',       sub: 'Translating clinical language into how it affects you.' },
  { label: 'Preparing questions for your doctor', sub: 'Surfacing what would be worth raising at your next appointment.' },
];

const UPLOAD_TIMEOUT_MS = 120000;            // 2 min — generous for cellular 10MB PDFs
const HANGING_AT_99_THRESHOLD_MS = 25000;    // After 25s at 99%, show "Stuck? Retry"

export const UploadProgressTile = ({ tileId, file, category, onComplete, onCancel }) => {
  const [stage, setStage] = useState('uploading');   // uploading | analyzing | completed | failed
  const [pct, setPct] = useState(0);
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [analyzeStep, setAnalyzeStep] = useState(0);
  const [hangingAt99, setHangingAt99] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const pollRef = useRef(null);
  const xhrRef = useRef(null);
  const startedRef = useRef(false);
  const hangTimerRef = useRef(null);

  // Single upload kickoff. Re-fires when retryNonce increments.
  const startUpload = useCallback(() => {
    setStage('uploading');
    setPct(0);
    setError(null);
    setHangingAt99(false);
    if (pollRef.current) clearInterval(pollRef.current);
    if (hangTimerRef.current) clearTimeout(hangTimerRef.current);

    const form = new FormData();
    form.append('file', file);
    form.append('category', category || 'other');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/documents/upload`);
    xhr.withCredentials = true;
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhrRef.current = xhr;

    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) {
        const next = Math.min(99, Math.round((evt.loaded / evt.total) * 100));
        setPct(next);
        if (next >= 99) {
          // Start the "stuck at 99" watchdog the moment we hit the wall.
          if (!hangTimerRef.current) {
            hangTimerRef.current = setTimeout(() => setHangingAt99(true), HANGING_AT_99_THRESHOLD_MS);
          }
        }
      }
    };

    xhr.onload = () => {
      if (hangTimerRef.current) { clearTimeout(hangTimerRef.current); hangTimerRef.current = null; }
      setHangingAt99(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const parsed = JSON.parse(xhr.responseText);
          setDoc(parsed);
          setPct(100);
          setStage('analyzing');
          startPolling(parsed.id);
        } catch {
          setStage('failed');
          setError('Could not read server response. Tap retry.');
        }
      } else {
        setStage('failed');
        try {
          const parsed = JSON.parse(xhr.responseText);
          setError(parsed?.detail || `Upload failed (status ${xhr.status})`);
        } catch {
          setError(`Upload failed (status ${xhr.status})`);
        }
      }
    };

    const failNetwork = (msg) => {
      if (hangTimerRef.current) { clearTimeout(hangTimerRef.current); hangTimerRef.current = null; }
      setHangingAt99(false);
      setStage('failed');
      setError(msg);
    };

    xhr.onerror = () => failNetwork('Network error — check your connection and tap retry.');
    xhr.ontimeout = () => failNetwork('Upload timed out. Try again on a stronger connection.');
    xhr.onabort = () => failNetwork('Upload was cancelled. Tap retry to try again.');
    xhr.upload.onerror = () => failNetwork('Network dropped during upload. Tap retry.');
    xhr.upload.ontimeout = () => failNetwork('Upload timed out. Try again on a stronger connection.');

    xhr.send(form);
  }, [file, category]);

  // Kick off upload once on mount, and any time the user taps Retry.
  useEffect(() => {
    if (retryNonce === 0) {
      if (startedRef.current) return;
      startedRef.current = true;
    }
    startUpload();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (hangTimerRef.current) clearTimeout(hangTimerRef.current);
      // Don't abort xhr on cleanup — React.StrictMode dev double-mount would kill in-flight uploads.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryNonce]);

  const startPolling = (docId) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/documents/${docId}`);
        setDoc(data);
        const status = data?.extraction_status;
        if (status === 'completed' || status === 'complete' || status === 'needs_review') {
          clearInterval(pollRef.current);
          setStage('completed');
          onComplete && onComplete(data);
        } else if (status === 'failed') {
          clearInterval(pollRef.current);
          setStage('failed');
          setError(data?.extraction_error || 'Analysis failed — tap retry.');
        }
      } catch {
        /* keep polling — transient */
      }
      // Safety: stop after ~2 min so we don't poll forever
      if (attempts > 60) {
        clearInterval(pollRef.current);
        setStage('failed');
        setError('Analysis is taking longer than expected. Tap retry.');
      }
    }, 2000);
  };

  // Terminal-success states auto-dismiss after a beat so the zone stays tidy.
  useEffect(() => {
    if (stage !== 'completed') return;
    const t = setTimeout(() => { onCancel && onCancel(tileId); }, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [stage]);

  // Rotate through analyse steps every 4s while analysis is in flight.
  useEffect(() => {
    if (stage !== 'analyzing') return;
    setAnalyzeStep(0);
    const interval = setInterval(() => {
      setAnalyzeStep((prev) => Math.min(prev + 1, ANALYZE_STEPS.length - 1));
    }, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const handleRetry = () => {
    setRetryNonce((n) => n + 1);
  };

  // Status-pill renderer — split out for clarity.
  const statusPill = () => {
    if (stage === 'uploading') {
      // Wave 2 fix: at 99% the bar is waiting for the server response.
      // Switch the label so the user sees motion at the boundary.
      const label = pct >= 99 ? 'Almost done…' : `Uploading · ${pct}%`;
      return (
        <span className="text-[11px] font-medium text-slate-500 tabular-nums" data-testid={`upload-stage-${pct >= 99 ? 'finishing' : 'uploading'}`}>
          {label}
        </span>
      );
    }
    if (stage === 'analyzing') {
      const step = ANALYZE_STEPS[analyzeStep] || ANALYZE_STEPS[0];
      return (
        <span className="text-[11px] font-medium text-[#2FB3A0] inline-flex items-center gap-1.5" data-testid={`upload-stage-analyzing-${analyzeStep}`}>
          <Spinner size={11} className="animate-spin" /> {step.label}…
        </span>
      );
    }
    if (stage === 'completed') {
      const needs = doc?.extraction_status === 'needs_review';
      return (
        <span className={`text-[11px] font-medium inline-flex items-center gap-1.5 ${needs ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="upload-stage-completed">
          <CheckCircle size={12} weight="fill" /> {needs ? 'Saved · needs review' : 'Saved to your timeline'}
        </span>
      );
    }
    return (
      <span className="text-[11px] font-medium text-red-600 inline-flex items-center gap-1.5" data-testid="upload-stage-failed">
        <Warning size={12} weight="fill" /> {error || 'Failed'}
      </span>
    );
  };

  return (
    <div
      className="bg-white border border-[#ECE8E1] rounded-xl p-3.5 flex items-center gap-3 shadow-[0_1px_2px_rgba(14,28,46,0.03)]"
      data-testid="upload-progress-tile"
    >
      <div className="w-10 h-10 bg-[#F4F1EB] rounded-lg flex items-center justify-center flex-shrink-0">
        {fileIcon(file.type)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-[#0E1C2E] truncate leading-tight" title={file.name}>
          {file.name}
        </p>
        <div className="mt-1">{statusPill()}</div>

        {/* Wave 2: visible "stuck?" hint with retry when 99% has held > 25s */}
        {stage === 'uploading' && hangingAt99 && (
          <div className="mt-1.5 flex items-center gap-2" data-testid="upload-stuck-prompt">
            <span className="text-[11px] text-amber-700">Taking longer than usual.</span>
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-[#0E1C2E] underline underline-offset-2 hover:text-[#2FB3A0] min-h-[24px]"
              data-testid="upload-retry-stuck"
            >
              <ArrowClockwise size={11} weight="bold" /> Retry
            </button>
          </div>
        )}

        {/* Sub-label + step pips during analysis — unchanged from Stage 20B */}
        {stage === 'analyzing' && (
          <>
            <p className="mt-1 text-[11px] text-slate-400 leading-snug" data-testid="upload-progress-sublabel">
              {(ANALYZE_STEPS[analyzeStep] || ANALYZE_STEPS[0]).sub}
            </p>
            <div className="mt-1.5 flex items-center gap-1" aria-hidden>
              {ANALYZE_STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all duration-500 ${i <= analyzeStep ? 'bg-[#2FB3A0]' : 'bg-[#ECE8E1]'}`}
                  style={{ width: i === analyzeStep ? '20px' : '10px' }}
                />
              ))}
            </div>
          </>
        )}

        {(stage === 'uploading' || stage === 'analyzing') && (
          <div className="mt-2 h-1 w-full bg-[#F4F1EB] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${stage === 'analyzing' ? 'bg-[#2FB3A0]' : 'bg-[#0E1C2E]'}`}
              style={{
                width: stage === 'analyzing' ? '100%' : `${pct}%`,
                animation: stage === 'analyzing' ? 'kindraPulse 1.6s ease-in-out infinite' : undefined,
              }}
            />
          </div>
        )}

        {/* Wave 2: explicit retry on terminal failure */}
        {stage === 'failed' && (
          <button
            type="button"
            onClick={handleRetry}
            className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-white bg-[#0E1C2E] hover:bg-[#1a2c44] rounded-lg px-3 py-1.5 min-h-[32px]"
            data-testid="upload-retry-failed"
          >
            <ArrowClockwise size={12} weight="bold" /> Retry upload
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => onCancel && onCancel(tileId)}
        className="p-1.5 text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 min-w-[32px] min-h-[32px] flex items-center justify-center"
        data-testid="upload-progress-dismiss"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default UploadProgressTile;
